import type { ReactElement } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Loading } from '@/components/shared/placeholders/Loading';
import { ErrorFallback } from '@/components/shared/placeholders/Error';
import type { GroupedAlertsResponse } from '@kirin/api-client/contracts/alerts';
import { AlertsTable, type AlertsTableColumn } from './AlertsTable';
import type { UseGroupedAlertsTabStateResult } from '@/hooks/api/useGroupedAlerts';

export interface GroupedAlertsTabContentProps<T extends string> {
  data: GroupedAlertsResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  state: UseGroupedAlertsTabStateResult<T>;
  columns: AlertsTableColumn<T>[];
  organizationId?: string;
}

/**
 * Shared card UI for the grouped alerts tab: loading, error, empty, or AlertsTable.
 * Used by both user (dashboard) and organization alerts tabs.
 */
export function GroupedAlertsTabContent<T extends string>({
  data,
  isLoading,
  isError,
  refetch,
  state,
  columns,
  organizationId,
}: GroupedAlertsTabContentProps<T>): ReactElement {
  const { page, setPage, sortBy, sortOrder, handleSort } = state;

  if (isLoading) {
    return <Loading label="Loading alerts" />;
  }

  if (isError) {
    return (
      <ErrorFallback retry={() => refetch()} label="Failed to load alerts." />
    );
  }

  if (!data) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No alerts found.
      </div>
    );
  }

  // Handle both legacy (alerts field) and table engine (data field) response shapes
  const alerts = 'data' in data ? data.data : data.alerts;

  return (
    <Card>
      <CardContent>
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No alerts found.
          </div>
        ) : (
          <AlertsTable
            alerts={alerts}
            pagination={data.pagination}
            page={page}
            onPrevious={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => p + 1)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            columns={columns}
            alertsBasePath={
              organizationId ? `/${organizationId}` : '/dashboard'
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

GroupedAlertsTabContent.displayName = 'GroupedAlertsTabContent';
